/**
 * Card displaying a single version's details with statistics.
 */

import { useState } from 'react';
import { Card, Typography, Space, Descriptions, Tag, Button, Popconfirm, message } from 'antd';
import { ClockCircleOutlined, UserOutlined, FileTextOutlined, DownOutlined, UpOutlined, DeleteOutlined } from '@ant-design/icons';
import ChangeRecordList from './ChangeRecordList';
import { deleteVersion } from '@/api/versions';
import type { RequirementVersion } from '@/types';
import dayjs from 'dayjs';
import { useT } from '@/i18n';

const { Text, Paragraph } = Typography;

interface VersionCardProps {
  version: RequirementVersion;
  projectId: number;
  isLatest?: boolean;
  onDeleted?: () => void;
}

const VersionCard: React.FC<VersionCardProps> = ({ version, projectId, isLatest, onDeleted }) => {
  const { t } = useT();
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const {
    id,
    version_number,
    version_info,
    publisher,
    published_at,
    statistics,
  } = version;

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('YYYY-MM-DD HH:mm:ss');
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteVersion(projectId, id);
      message.success(t('版本 {version} 已删除', { version: version_number }));
      onDeleted?.();
    } catch {
      message.error(t('删除失败，请重试'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card
      style={{ marginBottom: 16 }}
      title={
        <Space>
          <Tag color="blue">{version_number}</Tag>
          <ClockCircleOutlined />
          <Text type="secondary">{formatDate(published_at)}</Text>
        </Space>
      }
      extra={
        isLatest && (
          <Popconfirm
            title={t('确认删除版本 {version}？', { version: version_number })}
            description={t('删除后，该版本的变更记录将被清除，下次发布时会重新生成。')}
            onConfirm={handleDelete}
            okText={t('删除')}
            cancelText={t('取消')}
            okButtonProps={{ danger: true }}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              loading={deleting}
              size="small"
            >
              {t('删除版本')}
            </Button>
          </Popconfirm>
        )
      }
    >
      <Descriptions column={1} size="small">
        <Descriptions.Item
          label={
            <Space>
              <UserOutlined />
              <span>{t('发布人')}</span>
            </Space>
          }
        >
          {publisher.display_name || publisher.username}
        </Descriptions.Item>

        <Descriptions.Item
          label={
            <Space>
              <FileTextOutlined />
              <span>{t('版本信息')}</span>
            </Space>
          }
        >
          <Paragraph
            style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}
            ellipsis={{ rows: 3, expandable: true, symbol: t('展开') }}
          >
            {version_info}
          </Paragraph>
        </Descriptions.Item>

        <Descriptions.Item label={t('变更统计')}>
          <Space size="large">
            <Text>
              {t('总变更数:')} <Text strong>{statistics.total_changes}</Text>
            </Text>
            {statistics.created > 0 && (
              <Text>
                {t('新增:')} <Text type="success" strong>{statistics.created}</Text>
              </Text>
            )}
            {statistics.updated > 0 && (
              <Text>
                {t('修改:')} <Text type="warning" strong>{statistics.updated}</Text>
              </Text>
            )}
            {statistics.deleted > 0 && (
              <Text>
                {t('删除:')} <Text type="danger" strong>{statistics.deleted}</Text>
              </Text>
            )}
          </Space>
        </Descriptions.Item>
      </Descriptions>

      {/* 变更记录独立于 Descriptions 外，以使用完整卡片宽度 */}
      {statistics.total_changes > 0 ? (
        <div style={{ marginTop: 12 }}>
          <Button
            type="link"
            icon={expanded ? <UpOutlined /> : <DownOutlined />}
            onClick={() => setExpanded(!expanded)}
            style={{ padding: 0 }}
          >
            {expanded ? t('收起详细变更记录') : t('查看详细变更记录（含变更前后值）')}
          </Button>
          {expanded && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
              <ChangeRecordList projectId={projectId} versionId={id} />
            </div>
          )}
        </div>
      ) : (
        <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>{t('暂无变更记录')}</Text>
      )}
    </Card>
  );
};

export default VersionCard;
